import './Report.css';

import React, { useEffect, useState } from 'react';
import { formatCurrency, formatDate } from './utils/formatters';

// import InsuranceSection from './components/InsuranceSection';
// import TaxRegimeSuggestions from './components/TaxRegimeSuggestions';
import ActionPlan from './components/ActionPlan';
import Assumptions from './components/Assumptions';
import ContingencyPlanning from './components/ContingencyPlanning';
import DisclaimersAppendix from './components/DisclaimersAppendix';
import EmergencyPlanning from './components/EmergencyPlanning';
import FamilyIncomeExpenses from './components/IncomeExpenses';
import FinancialGoals from './components/FinancialGoals';
import ImplementationTips from './components/ImplementationTips';
import KeyRecommendations from './components/KeyRecommendations';
import MetricsToTrack from './components/MetricsToTrack';
// Import all the new components
import ReportHeader from './components/ReportHeader';
import SummarySection from './components/SummarySection';
import YourNetworth from './components/YourNetworth';
import { fetchReportWithFallback } from './Apis';
import mockData from './mockData.json';
import reportData from './test_report.json'; // Updated to use test_report.json

function Report() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Parse reportid from URL
        const params = new URLSearchParams(window.location.search);
        const reportId = params.get('reportid');

        fetchReportWithFallback(reportId, reportData)
            .then((data) => {
                setData(data);
                setLoading(false);
            })
            .catch((err) => {
                setError('Could not load report data.');
                setLoading(false);
            });
    }, []);

    if (loading) {
        return <div className="p-4">Loading report...</div>;
    }

    if (error) {
        return <div className="p-4 text-red-500">Error: {error}</div>;
    }

    if (!data) {
        return <div className="p-4">No data available.</div>;
    }

    const financialSnapshot = data.financial_snapshot || {};
    const income = financialSnapshot.income || {};
    const expenses = financialSnapshot.expenses || {};
    const executiveSummary = data.executive_summary || {};

    // Use monthly values directly from test_report.json
    const monthlyIncome = income.total_monthly_income || 0;
    const monthlyExpenses = expenses.total_monthly_expenses || 0;
    const netMonthlySavings = financialSnapshot.monthly_surplus !== undefined ? financialSnapshot.monthly_surplus : (monthlyIncome - monthlyExpenses);
    const savingsRate = monthlyIncome ? ((monthlyIncome - monthlyExpenses) / monthlyIncome) * 100 : 0;

    // Construct familyMembers array from test_report.json
    const client = data.personal_profile?.client;
    const spouse = data.personal_profile?.spouse;
    const familyMembers = [
        ...(client ? [{ name: client.name, age: client.age, role: 'Client', income: client.annual_income / 12, monthly: true }] : []),
        ...(spouse ? [{ name: spouse.name, age: spouse.age, role: 'Spouse', income: spouse.annual_income / 12, monthly: true }] : []),
        // Optionally, parse dependents from family_details if you want to show them
    ];

    // Construct otherIncomeSources array from test_report.json
    const otherIncomeSources = [
        { label: 'Business', value: income.business_income, icon: 'business', monthly: true },
        { label: 'Rental', value: income.rental_income, icon: 'rental', monthly: true },
        { label: 'Investment', value: income.investment_income, icon: 'stocks', monthly: true },
        { label: 'Other', value: income.other_income, icon: 'other', monthly: true },
    ];

    // Construct expenseChartData from test_report.json
    const expenseChartData = {
        data: {
            categories: ['Living', 'EMI'],
            series: [{ data: [expenses.living_expenses, expenses.emi_payments] }]
        },
        description: 'Breakdown of major expense categories'
    };

    // Prepare props for components that need mockData
    // const taxRegimeSuggestions = mockData.taxRegimeSuggestions;
    const emergencyPlanning = data.emergency_planning;
    // const insuranceReview = mockData.insuranceReview;
    const actionPlanData = data.action_plan || {};
    const mapAction = (item, typeLabel) => ({
        type: typeLabel,
        due: item.timeline || '',
        title: item.task || '',
        description: item.notes || '',
        priority: item.priority || 'Medium',
    });
    // Helper to select random N items from an array
    function getRandomItems(arr, n) {
        if (!Array.isArray(arr)) return [];
        const shuffled = arr.slice().sort(() => 0.5 - Math.random());
        return shuffled.slice(0, n);
    }

    const actionPlan = [
        ...getRandomItems(actionPlanData.immediate_actions || [], 2).map(item => mapAction(item, 'Immediate Action')),
        ...getRandomItems(actionPlanData.short_term_actions || [], 2).map(item => mapAction(item, 'Short Term Action')),
        ...getRandomItems(actionPlanData.long_term_actions || [], 2).map(item => mapAction(item, 'Long Term Action')),
    ];
    const metricsToTrack = data.metrics_to_track || [];
    const metricsNote = mockData.metricsNote;
    const contingencyPlanning = mockData.contingencyPlanning;
    const insuranceData = data.insurance_data;
    const assumptions = data.assumptions;
    const implementationTips = mockData.implementationTips;
    const savingsAllocation = data.savings_allocation
    const keyRecommendations = data.key_recommendations || {};
    // Construct financialGoals array from test_report.json
    const goalProjections = data.goal_analysis?.goal_projections || [];
    const currentYear = new Date().getFullYear();
    const financialGoals = goalProjections.map(g => {
        const years = g.target_year - currentYear;
        let status = 'in_progress';
        let completed = 0;
        if (g.status && g.status.toLowerCase() === 'behind') {
            status = years <= 0 ? 'not_started' : 'in_progress';
        }
        if (g.probability_of_achievement >= 100 || (g.status && g.status.toLowerCase() === 'on track')) {
            status = 'goal_reached';
            completed = 100;
        } else if (g.shortfall <= 0) {
            status = 'goal_reached';
            completed = 100;
        } else {
            // Estimate completion as % of current_value / required_value
            completed = Math.min(100, Math.round((g.current_value / g.required_value) * 100));
        }
        return {
            name: g.goal_name,
            target: g.required_value,
            targetText: `₹${g.required_value.toLocaleString()} in ${years > 0 ? years + ' years' : 'this year'}`,
            years,
            completed,
            status,
            reqSIP: g.monthly_sip_required,
            products: g.goal_type
        };
    });

    return (
        <div className="bg-gray-50 text-gray-800 min-h-screen">
            <div className="container mx-auto px-4 py-5 max-w-4xl lg:px-6">{/* More mobile-friendly padding */}
                <ReportHeader
                    data={data}
                    formatDate={formatDate}
                    advisorName={data?.metadata?.agent_name}
                    clientName={data.personal_profile?.client?.name || data.client_id}
                />

                <SummarySection executiveSummary={executiveSummary} />
                <KeyRecommendations executiveSummary={keyRecommendations} />
                <YourNetworth
                    executiveSummary={executiveSummary}
                    financialSnapshot={financialSnapshot}
                    formatCurrency={formatCurrency}
                />
                <FamilyIncomeExpenses
                    monthlyIncome={monthlyIncome}
                    monthlyExpenses={monthlyExpenses}
                    netMonthlySavings={netMonthlySavings}
                    savingsRate={savingsRate}
                    formatCurrency={formatCurrency}
                    familyMembers={familyMembers}
                    otherIncomeSources={otherIncomeSources}
                    expenseChartData={expenseChartData}
                    savingsAllocation={savingsAllocation}
                />
                {/* <TaxRegimeSuggestions suggestions={taxRegimeSuggestions} /> */}
                <EmergencyPlanning data={emergencyPlanning} />
                <FinancialGoals goals={financialGoals} />
                {/* <InsuranceSection data={insuranceReview} /> */}
                <ActionPlan items={actionPlan} />
                <MetricsToTrack metrics={metricsToTrack} note={metricsNote} />
                <ContingencyPlanning data={contingencyPlanning} insuranceData={insuranceData} />
                <Assumptions assumptions={assumptions} />
                <ImplementationTips tips={implementationTips.tips} documents={implementationTips.documents} />
                <DisclaimersAppendix />
            </div>
        </div>
    );
}

export default Report;
